import axiosInstance from '../variables/api/axiosInstance';

/**
 * mobileService - face back-office du canal mobile.
 *
 * Tout passe par le prefixe `/v1/mobile-backoffice`, JAMAIS par `/v1/mobile`.
 * Ce n'est pas une convention de nommage : le backend fait cohabiter deux chaines
 * Spring Security, et celle qui capte `/v1/mobile/**` n'accepte QUE des jetons
 * d'usager. Un appel d'agent vers `/v1/mobile/*` serait rejete avant meme
 * d'atteindre le controleur. Voir MobileBackOfficeController cote backend.
 *
 * Enveloppes : `DataResponse<T>` -> { data } ; `PaginatedResponse<T>` ->
 * { data, currentPage, pageSize, totalElements, totalPages, hasNext, hasPrevious }.
 * La pagination backend est 0-based, les tables antd 1-based : la conversion est
 * faite ici, une seule fois, et nulle part ailleurs.
 */

const unwrap = (res) => res?.data?.data;

const unwrapPage = (res) => {
  const body = res?.data || {};
  return {
    items: Array.isArray(body.data) ? body.data : [],
    page: (body.currentPage ?? 0) + 1,
    pageSize: body.pageSize ?? 20,
    total: body.totalElements ?? 0,
  };
};

const mobileService = {

  /* ═══════════ Declarations ═══════════ */

  /**
   * File des declarations a valider : dossiers de canal MOBILE au statut SOUMISE,
   * la plus ancienne d'abord (tri impose par le backend).
   *
   * Le backend n'expose ni recherche ni filtre de date sur cette route : c'est une
   * file d'attente qu'on vide, pas un moteur de recherche. Ne pas ajouter de params
   * ici en esperant qu'ils soient pris en compte -- ils seraient ignores en silence.
   *
   * Habilitation : MOBILE_DECLARATION_READ.
   */
  declarations: ({ page = 1, size = 20 } = {}) =>
    axiosInstance
      .get('/v1/mobile-backoffice/declarations', {
        params: { page: Math.max(page - 1, 0), size },
      })
      .then(unwrapPage),

  /**
   * Detail d'une declaration mobile. Meme DTO que la ligne de liste, avec les memes
   * champs -- l'ecran de detail en montre simplement davantage a la fois.
   *
   * N'est PAS restreint au statut SOUMISE : le backend n'exige que le canal MOBILE.
   * C'est ce qui permet a l'onglet « Declaration mobile » du detail sinistre de
   * continuer a servir ces donnees longtemps apres la validation.
   * Un dossier de canal WEB renvoie 400, pas 404.
   *
   * Habilitation : MOBILE_DECLARATION_READ.
   */
  detailDeclaration: (sinistreTrackingId) =>
    axiosInstance
      .get(`/v1/mobile-backoffice/declarations/${sinistreTrackingId}`)
      .then(unwrap),

  /**
   * Pieces du dossier vues par un agent (photos, constat, memo vocal).
   *
   * `ossanGedDocumentId` peut etre NUL tant que l'indexation GED n'est pas terminee,
   * ou si le moteur etait indisponible et que la piece est en repli local. Ce cas
   * doit s'afficher, pas produire un lien mort.
   *
   * Habilitation : MOBILE_DECLARATION_READ.
   */
  documentsDeclaration: (sinistreTrackingId) =>
    axiosInstance
      .get(`/v1/mobile-backoffice/declarations/${sinistreTrackingId}/documents`)
      .then((res) => unwrap(res) ?? []),

  /**
   * Valide une declaration : SOUMISE → NOUVEAU.
   *
   * C'est ici, et seulement ici, que le dossier entre dans le flux metier et que
   * les delais CEDEAO commencent a courir. L'observation est facultative.
   *
   * Habilitation : SINISTRE_VALIDER_DECLARATION.
   */
  validerDeclaration: (sinistreTrackingId, observation) =>
    axiosInstance
      .patch(`/v1/mobile-backoffice/declarations/${sinistreTrackingId}/valider`,
        observation ? { observation } : {})
      .then(unwrap),

  /**
   * Classe une declaration sans suite : SOUMISE → SANS_SUITE.
   *
   * Le motif est OBLIGATOIRE cote backend : un dossier ecarte sans raison ecrite
   * est indefendable devant la victime comme devant le regulateur.
   *
   * Habilitation : SINISTRE_VALIDER_DECLARATION.
   */
  rejeterDeclaration: (sinistreTrackingId, observation) =>
    axiosInstance
      .patch(`/v1/mobile-backoffice/declarations/${sinistreTrackingId}/rejeter`, { observation })
      .then(unwrap),

  /* ═══════════ Rattachements usager ↔ dossier ═══════════ */

  /** Habilitation : MOBILE_RATTACHEMENT_READ. */
  rattachements: ({ page = 1, size = 20 } = {}) =>
    axiosInstance
      .get('/v1/mobile-backoffice/rattachements', {
        params: { page: Math.max(page - 1, 0), size },
      })
      .then(unwrapPage),

  /**
   * Confirme ou refuse un rattachement. Acte NOMINATIF et NON REVERSIBLE : il ouvre
   * - ou ferme definitivement - l'acces d'un usager a un dossier d'indemnisation.
   * Toute UI appelant ceci doit demander confirmation explicite.
   *
   * Habilitation : MOBILE_RATTACHEMENT_CONFIRMER.
   */
  deciderRattachement: (rattachementTrackingId, payload) =>
    axiosInstance
      .patch(`/v1/mobile-backoffice/rattachements/${rattachementTrackingId}`, payload)
      .then(unwrap),

  /* ═══════════ Offres ═══════════ */

  /**
   * Publie une offre dans l'application. Refusee si la date limite est passee ou si
   * l'usager a deja repondu.
   *
   * Habilitation : OFFRE_PUBLIER_MOBILE.
   */
  publierOffre: (offreTrackingId, payload) =>
    axiosInstance
      .patch(`/v1/mobile-backoffice/offres/${offreTrackingId}/publier`, payload || {})
      .then(unwrap),

  /* ═══════════ Comptes usagers ═══════════ */

  /** Habilitation : MOBILE_USAGER_READ. N'expose ni empreinte de mot de passe ni compteur d'echecs. */
  usagers: ({ page = 1, size = 20 } = {}) =>
    axiosInstance
      .get('/v1/mobile-backoffice/usagers', {
        params: { page: Math.max(page - 1, 0), size },
      })
      .then(unwrapPage),

  /**
   * Enregistre le controle de la piece d'identite. Conditionne les actions
   * engageantes de l'usager, notamment sa reponse a une offre.
   *
   * Habilitation : MOBILE_USAGER_VERIFIER_IDENTITE.
   */
  verifierIdentite: (usagerTrackingId, payload) =>
    axiosInstance
      .patch(`/v1/mobile-backoffice/usagers/${usagerTrackingId}/identite`, payload)
      .then(unwrap),

  /* ═══════════ Fil de discussion avec l'usager ═══════════ */

  /**
   * Du plus recent au plus ancien. La consultation MARQUE COMME LUS les messages
   * de l'usager : ne pas appeler pour un simple compteur, cela eteindrait le signal
   * « message non lu » sans que personne ne l'ait lu.
   *
   * Habilitation : MOBILE_MESSAGE_READ.
   */
  messages: (sinistreTrackingId, { page = 1, size = 20 } = {}) =>
    axiosInstance
      .get(`/v1/mobile-backoffice/sinistres/${sinistreTrackingId}/messages`, {
        params: { page: Math.max(page - 1, 0), size },
      })
      .then(unwrapPage),

  /** Habilitation : MOBILE_MESSAGE_CREATE. */
  repondre: (sinistreTrackingId, payload) =>
    axiosInstance
      .post(`/v1/mobile-backoffice/sinistres/${sinistreTrackingId}/messages`, payload)
      .then(unwrap),

  /* ═══════════ GED ═══════════ */

  /** Telecharge une piece en blob. Endpoint GED existant, partage avec le reste de l'app. */
  telechargerDocument: (ossanGedDocumentId) =>
    axiosInstance
      .get(`/v1/ged/documents/${ossanGedDocumentId}/telecharger`, { responseType: 'blob' })
      .then((res) => res.data),
};

export default mobileService;
